import { Lightning } from '@lightningjs/sdk';

export class ScrollList extends Lightning.Component {
  static _states() {
    return [
      class Vertical extends this {
        $enter() {
          this.patch({
            Items: {
              flex: {
                direction: 'column',
              },
            },
          });
        }

        focusTo(idx) {
          if (this.focusedIndex <= idx) {
            this.focusedIndex = idx;
            this._shiftViewUp();
          } else if (this.focusedIndex > idx) {
            this.focusedIndex = idx;
            this._shiftViewDown();
          }
        }

        _shiftViewUp() {
          const focused = this._getFocused();

          if (focused) {
            const listBottom = -this.items.y + this.finalH;
            const focusedBottom = focused.finalY + focused.finalH;

            if (focusedBottom > listBottom || this.centerFocusedItem) {
              // Calculate offset
              let newY = focusedBottom - this.finalH;
              if (this.centerFocusedItem) newY += this.finalH / 2 - focused.finalH / 2;
              if (this._peekNext && this.items.childList.length > this.focusedIndex + 1) {
                newY += this.items.children[this.focusedIndex + 1].finalH / 2;
              }
              this.items.setSmooth('y', -newY);
            }
          }
        }

        _shiftViewDown() {
          const focused = this._getFocused();
          if (focused) {
            const index = this._firstFocusableIndex();
            if (index === this.focusedIndex && !this.centerFocusedItem) {
              this.items.setSmooth('y', 0);
            } else {
              const listTop = -this.items.y;
              const focusedTop = focused.finalY;

              if (focusedTop < listTop || this.centerFocusedItem) {
                // Calculate offset
                let newY = focusedTop;
                if (this.centerFocusedItem) newY -= this.finalH / 2 - focused.finalH / 2;
                if (this._peekNext && 0 < this.focusedIndex) {
                  newY -= this.items.children[this.focusedIndex - 1].finalH / 2;
                }
                this.items.setSmooth('y', -newY);
              }
            }
          }
        }

        _handleUp() {
          if (this.focusedIndex === 0) {
            return false;
          } else if (this._setFocusToPreviousVisible()) {
            this._shiftViewDown();
            return true;
          }
        }

        _handleDown() {
          if (this.focusedIndex === this.items.childList.length - 1) {
            return false;
          } else if (this._setFocusToNextVisible()) {
            this._shiftViewUp();
            return true;
          }
        }
      },
      class Horizontal extends this {
        $enter() {
          this.patch({
            Items: {
              flex: {
                direction: 'row',
              },
            },
          });
        }

        focusTo(idx) {
          if (this.focusedIndex <= idx) {
            this.focusedIndex = idx;
            this._shiftViewLeft();
          } else if (this.focusedIndex > idx) {
            this.focusedIndex = idx;
            this._shiftViewRight();
          }
        }

        //TODO implement centerFocusedItem for horizontal
        _shiftViewRight() {
          const focused = this._getFocused();

          if (focused) {
            const listLeft = -this.items.x;
            let margin = focused.flexItem ? focused.flexItem.marginLeft : 0;
            margin = margin ? margin : 0;
            const focusedLeft = focused.finalX - margin;

            if (focusedLeft < listLeft) {
              // Calculate offset
              let newX = focusedLeft;
              if (this._peekNext && 0 < this.focusedIndex) {
                newX -= this.items.children[this.focusedIndex - 1].finalW / 2;
              }
              this.items.setSmooth('x', -newX);
            }
          }
        }

        _shiftViewLeft() {
          const focused = this._getFocused();
          if (focused) {
            const listRight = -this.items.x + this.finalW;
            const focusedRight = focused.finalX + focused.finalW;

            if (listRight < focusedRight) {
              // Calculate offset
              let newX = focusedRight - this.finalW;
              if (this._peekNext && this.items.childList.length > this.focusedIndex + 1) {
                newX += this.items.children[this.focusedIndex + 1].finalW / 2;
              }
              this.items.setSmooth('x', -newX);
            }
          }
        }

        _handleLeft() {
          if (this.focusedIndex === 0) {
            return false;
          } else if (this._setFocusToPreviousVisible()) {
            this._shiftViewRight();
            return true;
          }
        }

        _handleRight() {
          if (this.focusedIndex === this.items.childList.length - 1) {
            return false;
          } else if (this._setFocusToNextVisible()) {
            this._shiftViewLeft();
            return true;
          }
        }
      },
    ];
  }

  _init() {
    this.focusedIndex = 0;
    if (!this.dir) {
      this._setState('Vertical');
    }
  }

  _setFocusToNextVisible() {
    for (let i = this.focusedIndex + 1, last = this.items.childList.length - 1; i <= last; i++) {
      if (this.items.children[i].visible && !this.items.children[i].unfocusable) {
        this.focusedIndex = i;
        return true;
      }
    }
    return false;
  }

  _setFocusToPreviousVisible() {
    for (let i = this.focusedIndex - 1; i >= 0; i--) {
      if (this.items.children[i].visible && !this.items.children[i].unfocusable) {
        this.focusedIndex = i;
        return true;
      }
    }
    return false;
  }

  set direction(val) {
    this.dir = val;
    if (val === 'row') {
      this._setState('Horizontal');
    } else if (val === 'column') {
      this._setState('Vertical');
    }
  }

  /**
   * When shifting, shift a bit extra to peek the next item in the list
   * @param val
   */
  set peekNext(val) {
    this._peekNext = val;
  }

  get items() {
    return this.tag('Items');
  }

  clearItems() {
    this.items.childList.clear();
    this.items.x = 0;
    this.items.y = 0;
    this.focusedIndex = 0;
  }

  focusLastVisible() {
    for (let i = this.items.childList.length - 1; i >= 0; i--) {
      if (this.items.children[i].visible && !this.items.children[i].unfocusable) {
        this.focusedIndex = i;
        this._refocus();
        return;
      }
    }
  }

  focusFirstVisible() {
    const index = this._firstFocusableIndex();
    if (index !== -1) {
      this.focusedIndex = index;
      this._refocus();
    }
  }

  _firstFocusableIndex() {
    for (let i = 0; i < this.items.childList.length; i++) {
      if (this.items.children[i].visible && !this.items.children[i].unfocusable) {
        return i;
      }
    }
    return -1;
  }

  _getFocused() {
    if (this.focusedIndex < this.items.children.length) {
      return this.items.children[this.focusedIndex];
    }
    return null;
  }

  set focusIndex(index) {
    const firstIndex = this._firstFocusableIndex();
    if (firstIndex === -1) return;
    const last = this.items.childList.length;
    if (firstIndex < index < last) {
      this.focusedIndex = index;
    }
  }
}
